import type { AgentRun } from '@lucidtech/las-sdk-browser';
import { useMatchRoute } from '@tanstack/react-router';
import {
  AccessorKeyColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { merge } from '@/components';
import { Checkbox, Flex } from '@/components/core';
import { ArrowShortIcon, ChevronSelectorIcon } from '@/components/icons';
import { useFilteredAgentRunsQuery } from '@/hooks/api';
import { SortingUpdates, useInboxStore } from '@/store';
import { DivProps, TableProps, TableRowProps } from '@/utils';

import { AgentRunsEmpty } from './AgentRunsEmpty.tsx';
import { DateFilterCard } from './DateFilterCard.tsx';
import { DateRangeFilterCard } from './DateRangeFilterCard.tsx';
import { RunsRow } from './RunsRow.tsx';
import { SelectedRunId } from './SelectedRunId';
import { StringFilterCard } from './StringFilterCard.tsx';

const filterGroupToLocation = (filterGroup: number) => {
  return ({
    1: 'review',
    2: 'exported',
    3: 'archived',
    4: 'review-completed',
  }[filterGroup] ?? 'review') as 'review' | 'exported' | 'archived' | 'review-completed';
};

export type AgentRunsTableProps = TableProps & {
  agentId?: string;
  hideHeaders?: boolean;
  hideSelectionColumn?: boolean;
  columns: AccessorKeyColumnDef<AgentRun>[];
  props?: {
    outerContainer?: DivProps;
    row?: TableRowProps;
  };
};

const IAgentRunsTable = ({
  className,
  agentId,
  hideHeaders,
  hideSelectionColumn,
  columns,
  props,
  ...rest
}: AgentRunsTableProps) => {
  const sorting = useInboxStore((state) => state.sorting);
  const updateSorting = useInboxStore((state) => state.updateSorting);
  const filters = useInboxStore((state) => state.filters);
  const selectedFilterGroup = useInboxStore((state) => state.selectedFilterGroup);
  const setSelectedRunIds = useInboxStore((state) => state.setSelectedRunIds);
  const userIsFetching = useInboxStore((state) => state.isFetching);

  const matchRoute = useMatchRoute();
  const matchedRoute = matchRoute({ to: '/details/agents/$agentId/runs/$runId' });

  const { data, fetchNextPage, isFetching } = useFilteredAgentRunsQuery({ agentId });
  const runs = useMemo(() => data?.pages?.flatMap((page) => page.data) ?? [], [data]);
  const nextToken = useMemo(() => data?.pages?.at(-1)?.nextToken, [data?.pages]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight < window.innerHeight * 0.9 && !isFetching && nextToken) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetching, nextToken]
  );

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const columns_ = useMemo<AccessorKeyColumnDef<AgentRun>[]>(() => {
    const leftColumns: AccessorKeyColumnDef<AgentRun>[] = hideSelectionColumn
      ? []
      : [
          {
            accessorKey: 'runId',
            header: () => (
              <Checkbox
                onChange={(selected) =>
                  selected ? setSelectedRunIds(runs.map((r) => r.runId)) : setSelectedRunIds([])
                }
              />
            ),
            id: 'selected',
            cell: ({ row }) => <SelectedRunId runId={row.original.runId} />,
            size: 40,
            enableSorting: false,
          },
        ];
    return [...leftColumns, ...columns];
  }, [columns, hideSelectionColumn, runs, setSelectedRunIds]);

  const sortingState = useMemo(() => sorting.filter((s) => !s.hidden), [sorting]);

  const table = useReactTable({
    data: runs,
    columns: columns_,
    state: {
      sorting: sortingState,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    isMultiSortEvent: () => true,
    manualSorting: false,
  });

  const { rows } = table.getRowModel();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 40,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 20,
  });

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const updates: SortingUpdates = {};
      for (const s of sorting.filter((s) => !s.hidden)) {
        updates[s.id] = { ...s, hidden: true };
      }
      for (const update of updater(sortingState) as SortingState) {
        updates[update.id] = { hidden: false, desc: update.desc };
      }
      if (JSON.stringify(updates) !== JSON.stringify(sorting)) {
        updateSorting(updates);
        if (rows.length) {
          rowVirtualizer.scrollToIndex?.(0);
        }
      }
    },
    [rowVirtualizer, sorting, sortingState, rows, updateSorting]
  );

  table.setOptions((prev) => ({
    ...prev,
    onSortingChange: handleSortingChange,
  }));

  const { className: outerContainerClassName, ...outerContainerProps } = props?.outerContainer ?? {};

  return (
    <>
      {!hideHeaders && (
        <Flex className="gap-md items-center">
          {filters.map((f) => (
            <th key={f.id}>
              {f.type === 'date-range' ? (
                <DateRangeFilterCard columns={columns.map((c) => c.accessorKey)} filter={f} />
              ) : null}
              {f.type === 'date' ? <DateFilterCard columns={columns.map((c) => c.accessorKey)} filter={f} /> : null}
              {f.type === 'string' ? <StringFilterCard columns={columns.map((c) => c.accessorKey)} filter={f} /> : null}
            </th>
          ))}
        </Flex>
      )}

      {rowVirtualizer.getTotalSize() > 0 ? (
        <div
          className={merge('w-full overflow-auto rounded-lg border-1 border-gray-200', outerContainerClassName)}
          ref={tableContainerRef}
          onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
          {...outerContainerProps}
        >
          <table className={merge('grid', className)} {...rest}>
            {!hideHeaders ? (
              <thead className="sticky top-0 z-10 grid border-b border-gray-200 bg-gray-50 text-xs text-gray-500">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="p-sm px-md flex w-full items-center">
                    {headerGroup.headers.map((header) => {
                      return (
                        <th className="flex" key={header.id} style={{ width: header.getSize() }}>
                          {header.column.getCanSort() ? (
                            <div
                              className={merge('flex cursor-pointer flex-row gap-2 select-none')}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {{
                                asc: <ArrowShortIcon rotation="up" />,
                                desc: <ArrowShortIcon rotation="down" />,
                              }[header.column.getIsSorted() as string] ?? <ChevronSelectorIcon />}
                            </div>
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
            ) : null}
            <tbody className="relative grid" style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index] as Row<AgentRun>;
                // TODO: Should selected state be a property on row.original ?
                const selected = matchedRoute && matchedRoute.runId === row.original.runId;

                return (
                  <RunsRow
                    key={virtualRow.index}
                    virtualRow={virtualRow}
                    row={row}
                    selected={selected}
                    runId={row.original.runId}
                    agentId={agentId}
                    measureFn={rowVirtualizer.measureElement}
                    rowProps={props?.row}
                    isFetching={userIsFetching}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <AgentRunsEmpty
          agentId={agentId}
          location={filterGroupToLocation(selectedFilterGroup)}
          showDropzone={selectedFilterGroup === 0}
        />
      )}
    </>
  );
};

export const AgentRunsTable = memo(IAgentRunsTable) as typeof IAgentRunsTable;
